import React from "react";
import { openCalculator, displayDate } from "@/lib/evidencePresentation";

const money = (value) =>
  typeof value === "number" && Number.isFinite(value)
    ? new Intl.NumberFormat("en-AU", {
        style: "currency",
        currency: "AUD",
        maximumFractionDigits: 0,
      }).format(value)
    : "Not calculated";
export default function DecisionMetrics({ calculations, propertyType }) {
  const { purchase, cashflow } = calculations || {};
  const entries = [
    [
      "Cash required",
      cashflow?.base?.cash_needed ?? purchase?.costs?.cash_needed,
      cashflow ? "cashflow-calculator" : "purchase-calculator",
      "Total contribution, including contingency and any deposit already paid.",
    ],
    [
      "Transfer duty",
      purchase?.costs?.total_duty,
      "purchase-calculator",
      "Includes foreign surcharge where selected; eligibility is not assessed.",
    ],
    [
      "Gross yield",
      cashflow?.base?.gross_yield_pct,
      "cashflow-calculator",
      "Weekly assumed rent × 52 ÷ entered price. Before vacancy and costs.",
    ],
    [
      "Weekly cash flow",
      cashflow?.base?.weekly_cashflow,
      "cashflow-calculator",
      "Pre-tax, after assumed operating costs and loan repayments.",
    ],
  ];
  return (
    <section className="decision-section" aria-label="Decision metrics">
      <div className="section-heading">
        <span className="eyebrow">Your numbers, made clear</span>
        <h2>What does ownership look like?</h2>
        <p>
          Build a scenario with your own quotes and assumptions. Missing
          calculations remain blank.
        </p>
      </div>
      <div className="decision-grid">
        {entries.map(([label, value, target, scope]) => (
          <article key={label}>
            <span>{label}</span>
            <strong className={value === undefined || value === null ? "awaiting-input" : ""}>
              {value === undefined || value === null
                ? propertyType === "house_and_land" ||
                  (propertyType === "land" && target === "cashflow-calculator")
                  ? "Not modelled"
                  : target === "purchase-calculator"
                    ? "Add purchase details"
                    : "Add cash-flow details"
                : label === "Gross yield"
                  ? `${value.toFixed(2)}%`
                  : money(value)}
            </strong>
            <p>{scope}</p>
            {propertyType === "house_and_land" ||
            (propertyType === "land" && target === "cashflow-calculator") ? (
              <small>Not modelled for this property type.</small>
            ) : (
              <button
                type="button"
                className="inline-action"
                onClick={() => openCalculator(target)}
              >
                {value === undefined || value === null
                  ? "Add assumptions →"
                  : "Review calculation →"}
              </button>
            )}
          </article>
        ))}
      </div>
      {(purchase || cashflow) && (
        <p className="source">
          Calculated from your inputs ·{" "}
          {cashflow?.calc_version || purchase?.calc_version} · Calculated{" "}
          {displayDate(cashflow?.calculated_at || purchase?.calculated_at)}
          {purchase?.rules_checked_on
            ? ` · Duty rules checked ${displayDate(purchase.rules_checked_on)}`
            : ""}
          . <a href="#costs">Review assumptions, formulas and sources</a>.
        </p>
      )}
      {cashflow?.stress_tests && (
        <div className="stress-summary">
          <h3>What if things change?</h3>
          <p className="source">
            Weekly pre-tax cash flow under each separate stress scenario. These
            are calculations, not forecasts.
          </p>
          <div>
            {[
              ["rate_plus_2pct", "Interest rate +2 percentage points"],
              ["rent_minus_10pct", "Rent −10%"],
            ].map(([key, label]) => (
              <article key={key}>
                <span>{label}</span>
                <strong>
                  {money(cashflow.stress_tests[key]?.weekly_cashflow)}
                </strong>
                <small> / week</small>
              </article>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
